import { Camera, CheckCircle2, Crosshair, RotateCcw, Save, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { clearPlacement, savePlacement } from "../lib/api";
import { uploadPlacementPhoto } from "../lib/firebaseClient";
import { flushQueue, queuePlacement, readQueue, type QueuedPlacement } from "../lib/offlineQueue";
import type { PlacementPayload, Poster } from "../lib/types";

interface LoggerProps {
  posters: Poster[];
  onSaved: () => Promise<void>;
}

type SaveState = "idle" | "saving" | "queued" | "saved" | "error";
type LoggerLocation = { lat: number; lng: number; accuracy?: number };

export function Logger({ posters, onSaved }: LoggerProps) {
  const [posterId, setPosterId] = useState("");
  const [location, setLocation] = useState<LoggerLocation | null>(null);
  const [notes, setNotes] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [venueType, setVenueType] = useState("");
  const [venue, setVenue] = useState("");
  const [visibilityRating, setVisibilityRating] = useState(4);
  const [photo, setPhoto] = useState<File | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueuedPlacement[]>(readQueue());
  const [scannerOpen, setScannerOpen] = useState(false);
  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const scannerRegionId = useRef(`qr-reader-${crypto.randomUUID()}`);

  const poster = useMemo(
    () => posters.find((item) => item.posterId.toUpperCase() === posterId.trim().toUpperCase()),
    [posterId, posters]
  );

  useEffect(() => {
    const update = () => setQueue(readQueue());
    window.addEventListener("posterly-queue", update);
    window.addEventListener("online", () => void syncQueue());
    return () => {
      window.removeEventListener("posterly-queue", update);
      window.removeEventListener("online", () => void syncQueue());
    };
  }, []);

  useEffect(() => {
    if (!scannerOpen) return undefined;
    let scanner: import("html5-qrcode").Html5Qrcode | undefined;
    let cancelled = false;
    let stopped = false;

    async function stopScanner() {
      if (!scanner || stopped) return;
      stopped = true;
      try {
        await scanner.stop();
      } catch {
        // The scanner may already be stopped after a successful decode.
      }
      try {
        scanner.clear();
      } catch {
        // The region can disappear during React unmount on mobile browsers.
      }
    }

    async function startScanner() {
      try {
        setError(null);
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;

        scanner = new Html5Qrcode(scannerRegionId.current);
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            const parsed = parseQrValue(decodedText);
            setPosterId(parsed);
            setLastScanned(parsed);
            await stopScanner();
            setScannerOpen(false);
          },
          () => undefined
        );
      } catch (scanError) {
        setScannerOpen(false);
        setError(scanError instanceof Error ? scanError.message : "Unable to start camera scanner.");
      }
    }

    void startScanner();

    return () => {
      cancelled = true;
      void stopScanner();
    };
  }, [scannerOpen]);

  function parseQrValue(value: string) {
    try {
      const url = new URL(value);
      const pathId = url.pathname.split("/").filter(Boolean).pop();
      const queryId = url.searchParams.get("poster");
      return (queryId || pathId || value).toUpperCase();
    } catch {
      return value.toUpperCase();
    }
  }

  function getLocation() {
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (position) =>
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        }),
      (geoError) => setError(geoError.message),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
  }

  function buildPayload(photoUrl?: string): PlacementPayload {
    if (!location) throw new Error("GPS location is required");
    return {
      posterId: posterId.trim().toUpperCase(),
      lat: location.lat,
      lng: location.lng,
      accuracy: location.accuracy,
      notes,
      photoUrl,
      tags: {
        neighborhood,
        venueType,
        venue,
        visibilityRating
      }
    };
  }

  async function submit() {
    setSaveState("saving");
    setError(null);
    try {
      let photoUrl: string | undefined;
      if (photo) photoUrl = await uploadPlacementPhoto(posterId.trim().toUpperCase(), photo);
      const payload = buildPayload(photoUrl);
      if (!navigator.onLine) {
        queuePlacement(payload);
        setSaveState("queued");
        return;
      }
      await savePlacement(payload);
      await onSaved();
      setSaveState("saved");
    } catch (submitError) {
      setSaveState("error");
      setError(submitError instanceof Error ? submitError.message : "Unable to save placement");
    }
  }

  function loadExistingPlacement() {
    if (!poster) return;
    setPosterId(poster.posterId);
    setLocation(
      typeof poster.placementLat === "number" && typeof poster.placementLng === "number"
        ? { lat: poster.placementLat, lng: poster.placementLng, accuracy: poster.placementAccuracy }
        : null
    );
    setNotes(poster.notes ?? "");
    setNeighborhood(poster.tags?.neighborhood ?? "");
    setVenueType(poster.tags?.venueType ?? "");
    setVenue(poster.tags?.venue ?? "");
    setVisibilityRating(poster.tags?.visibilityRating ?? 4);
    setPhoto(null);
    setSaveState("idle");
    setError(null);
  }

  async function clearCurrentPlacement() {
    if (!poster || !poster.isPlaced) return;
    const confirmed = window.confirm(`Clear placement data for ${poster.posterId}? This keeps the poster and scan history.`);
    if (!confirmed) return;
    setSaveState("saving");
    setError(null);
    try {
      await clearPlacement(poster.posterId);
      await onSaved();
      reset();
      setSaveState("saved");
    } catch (clearError) {
      setSaveState("error");
      setError(clearError instanceof Error ? clearError.message : "Unable to clear placement");
    }
  }

  async function syncQueue() {
    if (!navigator.onLine || queue.length === 0) return;
    await flushQueue(savePlacement);
    await onSaved();
    setQueue(readQueue());
  }

  function reset() {
    setPosterId("");
    setLocation(null);
    setNotes("");
    setNeighborhood("");
    setVenueType("");
    setVenue("");
    setVisibilityRating(4);
    setPhoto(null);
    setSaveState("idle");
    setError(null);
    setLastScanned(null);
  }

  return (
    <main className="logger-layout">
      <section className="logger-primary">
        <div className="section-heading">
          <h1>Log New Placement</h1>
          <p>Scan the printed QR, capture GPS, add context, and save the placement.</p>
        </div>

        <div className="logger-actions">
          <button className="primary-button" onClick={() => setScannerOpen(true)}>
            <Camera size={18} /> Scan QR
          </button>
          <button className="secondary-button" onClick={getLocation}>
            <Crosshair size={18} /> Use current location
          </button>
        </div>

        {scannerOpen ? (
          <div className="scanner-panel">
            <div id={scannerRegionId.current} />
            <button className="secondary-button" onClick={() => setScannerOpen(false)}>
              Close scanner
            </button>
          </div>
        ) : null}

        {lastScanned ? <div className="success-box">Scanned {lastScanned}. Confirm the details and save the placement.</div> : null}

        <label>
          Poster ID
          <input value={posterId} onChange={(event) => setPosterId(event.target.value.toUpperCase())} placeholder="MOD-047" />
        </label>

        {poster ? (
          <div className="poster-confirm">
            <CheckCircle2 size={20} />
            <div>
              <strong>{poster.posterId}</strong>
              <span>{poster.designType} · {poster.isPlaced ? "already placed" : "ready to place"}</span>
            </div>
          </div>
        ) : posterId ? (
          <div className="warning-box">Poster not found in the current data set.</div>
        ) : null}

        {poster?.isPlaced ? (
          <div className="logger-actions">
            <button className="secondary-button" onClick={loadExistingPlacement}>
              Edit existing placement
            </button>
            <button className="danger-button" disabled={saveState === "saving"} onClick={() => void clearCurrentPlacement()}>
              Clear placement
            </button>
          </div>
        ) : null}

        <div className="form-grid">
          <label>
            Neighborhood
            <input value={neighborhood} onChange={(event) => setNeighborhood(event.target.value)} placeholder="Downtown" />
          </label>
          <label>
            Venue type
            <input value={venueType} onChange={(event) => setVenueType(event.target.value)} placeholder="Campus, cafe, gym" />
          </label>
          <label>
            Venue
            <input value={venue} onChange={(event) => setVenue(event.target.value)} placeholder="Main library entrance" />
          </label>
          <label>
            Visibility
            <input
              type="range"
              min="1"
              max="5"
              value={visibilityRating}
              onChange={(event) => setVisibilityRating(Number(event.target.value))}
            />
            <span className="range-value">{visibilityRating}/5</span>
          </label>
        </div>

        <label>
          Notes
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Placement details, permission notes, nearby landmark" />
        </label>

        <label className="file-input">
          <Upload size={18} />
          <span>{photo ? photo.name : "Optional placement photo"}</span>
          <input type="file" accept="image/*" capture="environment" onChange={(event) => setPhoto(event.target.files?.[0] ?? null)} />
        </label>

        {location ? (
          <div className="gps-box">
            GPS locked: {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
            {typeof location.accuracy === "number" ? ` · ±${Math.round(location.accuracy)}m` : ""}
          </div>
        ) : null}

        {error ? <div className="error-box">{error}</div> : null}

        <div className="logger-actions">
          <button className="primary-button" disabled={!posterId || !location || saveState === "saving"} onClick={() => void submit()}>
            <Save size={18} /> {saveState === "saving" ? "Saving..." : "Save placement"}
          </button>
          <button className="secondary-button" onClick={reset}>
            <RotateCcw size={18} /> Reset
          </button>
        </div>

        {saveState === "saved" ? <div className="success-box">Placement saved. Ready for the next poster.</div> : null}
        {saveState === "queued" ? <div className="success-box">Placement queued offline and will sync later.</div> : null}
      </section>

      <aside className="logger-side">
        <h2>Queue</h2>
        <p>{queue.length} offline placement{queue.length === 1 ? "" : "s"}</p>
        <button className="secondary-button" disabled={queue.length === 0} onClick={() => void syncQueue()}>
          Sync now
        </button>
        <h2>Recent posters</h2>
        <div className="poster-list compact">
          {posters.slice(0, 8).map((item) => (
            <button key={item.posterId} onClick={() => setPosterId(item.posterId)}>
              <strong>{item.posterId}</strong>
              <span>{item.designType}</span>
            </button>
          ))}
        </div>
      </aside>
    </main>
  );
}
